/**
 * Right column of the booking pages: the quote being booked, with its legs,
 * facts and charges. Prices are estimates, as everywhere in the Lane planner.
 */
import React from 'react';
import dayjs from 'dayjs';

import type { NormalizedQuote } from '../../../apis/lanePlanner';
import { formatLegTime } from './localTime';
import CarrierBadge from './CarrierBadge';
import {
  carrierNameOf,
  carrierOf,
  formatMoney,
  logosOf,
  travelTextOf,
} from './quoteView';
import styles from './styles.module.css';

const Row: React.FC<{
  label: string;
  value: string | null;
  strong?: boolean;
}> = ({ label, value, strong }) =>
  value ? (
    <div
      className={`${styles.detailRow} ${strong ? styles.detailRowTotal : ''}`}
    >
      <span>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  ) : null;

const BookingSummary: React.FC<{
  quote: NormalizedQuote;
  vendorName: string;
}> = ({ quote, vendorName }) => {
  const carriers = [...new Set(quote.legs.map((leg) => leg.carrier))]
    .filter(Boolean)
    .join(', ');
  return (
    <aside className={styles.bookingSide} aria-label='Quote being booked'>
      <div className={styles.bookingCard}>
        <div className={styles.rateCarrierCell}>
          {logosOf(quote).map((url) => (
            <img key={url} src={url} alt='' className={styles.carrierLogo} />
          ))}
          {!logosOf(quote).length && carrierNameOf(quote) && (
            <CarrierBadge name={carrierNameOf(quote) ?? ''} mode={quote.mode} />
          )}
          <div className={styles.rateCarrierText}>
            <div className={styles.rateName}>
              {carriers || carrierOf(quote)}
            </div>
            <div className={styles.rateCellSub}>
              Ref {quote.vendor_quote_id} · via {vendorName}
            </div>
          </div>
        </div>
        {quote.legs.map((leg, index) => (
          <div
            // legs have no id of their own and the list never reorders
            // eslint-disable-next-line react/no-array-index-key
            key={`${leg.service_ref ?? ''}-${index}`}
            className={styles.bookingLeg}
          >
            <div>
              <div className={styles.rateCellValue}>
                {leg.origin ?? '—'} → {leg.destination ?? '—'}
              </div>
              <div className={styles.rateCellSub}>
                {[leg.carrier, leg.service_ref].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className={styles.rateCellSub}>
              {formatLegTime(quote, index, 'departs') ?? ''}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.bookingCard}>
        <Row label='Transit' value={travelTextOf(quote)} />
        <Row label='Temperature' value={quote.temperature_range} />
        <Row label='Monitoring' value={quote.monitoring} />
        {quote.charges.map((charge, index) => (
          <Row
            // charges have no id of their own and the list never reorders
            // eslint-disable-next-line react/no-array-index-key
            key={`${charge.label}-${index}`}
            label={charge.label}
            value={formatMoney(charge.amount, quote.currency)}
          />
        ))}
        <Row
          label='Total'
          value={formatMoney(quote.price, quote.currency)}
          strong
        />
        <div className={`${styles.partialNote} ${styles.summaryNote}`}>
          Estimated rate. Final invoice may vary with actual weight and
          accessorials.
          {quote.valid_until &&
            ` Quote valid until ${dayjs(quote.valid_until).format('M/D/YY')}.`}
        </div>
      </div>
    </aside>
  );
};

export default BookingSummary;
